import * as THREE from 'three';
import { clearViewLines } from "@/utils/viewAnalyser"
import { runViewAnalysisForAllWindows, ViewAnalysisProgress } from "@/utils/viewAnalysis"
import { WeaveButton } from "../UI/WeaveButton/WeaveButton"
import { useViewScoreStore } from '@/store/viewScoreStore';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { MutableRefObject, useState } from 'react';
import styles from './styles.module.scss';
import { WeaveAccordion } from '../UI/WeaveAccordion/WeaveAccordion';
import { BeyondHorizonSettings } from './BeyondHorizonSettings';
import { useWorkflowStore } from '@/store/workflowStore';
import { WeaveToggle } from '../UI/WeaveToggle/WeaveToggle';

interface ViewControlProps {
	sceneRef: MutableRefObject<THREE.Scene | null>;
	cameraRef: MutableRefObject<THREE.Camera | null>;
	selectedBuildingId: string | null;
}

export const ViewControls = (props: ViewControlProps) => {
	const {
		sceneRef,
		cameraRef,
		selectedBuildingId,
	} = props;

	const [isRunning, setIsRunning] = useState(false);
	const [progress, setProgress] = useState<ViewAnalysisProgress>({ current: 0, total: 0, windowName: '' });
	const { viewData } = useViewScoreStore();
	const hasViewData = Object.keys(viewData).length > 0;
	const { isFacadeViewAnalysisMode, setIsFacadeViewAnalysisMode } = useThreeSceneStore();
	const { currentStage } = useWorkflowStore();

	const runViewAnalysis = async () => {
		if (!sceneRef.current || !cameraRef.current) {
			console.warn('Scene or camera not available');
			return;
		}

		if (!(cameraRef.current instanceof THREE.PerspectiveCamera)) {
			console.warn('Camera must be a PerspectiveCamera for view analysis');
			return;
		}

		const scene = sceneRef.current;
		const camera = cameraRef.current as THREE.PerspectiveCamera;

		setIsRunning(true);

		try {
			await runViewAnalysisForAllWindows(camera, scene, selectedBuildingId ?? undefined, (progressUpdate) => {
				setProgress(progressUpdate);
			});
		} catch (error) {
			console.error('Error running view analysis:', error);
		} finally {
			setIsRunning(false);
		}
	};

	if (!sceneRef.current) {
		return null;
	}

	return (
		<div className={styles.container}>
			<WeaveAccordion label="Horizon Settings">
				<BeyondHorizonSettings />
			</WeaveAccordion>
			<label className={styles.facadeToggle}>
				Facade point view analysis
				<WeaveToggle
					toggled={isFacadeViewAnalysisMode}
					onChange={() => setIsFacadeViewAnalysisMode(!isFacadeViewAnalysisMode)}
				>
				</WeaveToggle>
			</label>
			<div className={styles.controls}>
				<WeaveButton
					variant="solid"
					onClick={runViewAnalysis}
					disabled={isRunning || currentStage === 'predesign_programming'}
				>
					{isRunning
						? `Analyzing... (${progress.current}/${progress.total})`
						: 'Run View Analysis for All Windows'
					}
				</WeaveButton>
				<WeaveButton
					onClick={() => clearViewLines(sceneRef.current!)}
					disabled={!hasViewData}
				>
					Clear Visualization
				</WeaveButton>
			</div>
		</div>
	)
}